import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, DollarSign, Package, Eye, ShoppingCart } from 'lucide-react';
import { useVendor } from '../context/VendorContext';
import { Header } from '../components/ui/Header';
export function VendorAnalyticsScreen() {
  const {
    orders,
    products
  } = useVendor();
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('7d');
  // Calculate metrics
  const totalRevenue = orders.filter(o => o.paymentStatus === 'Paid').reduce((sum, o) => sum + o.total, 0);
  const totalOrders = orders.length;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const conversionRate = 12.5; // Mock data
  const totalViews = 1234; // Mock data
  // Top selling products
  const topProducts = products.slice(0, 5);
  // Sales data for simple bar chart
  const salesData = [{
    day: 'Mon',
    amount: 450000
  }, {
    day: 'Tue',
    amount: 680000
  }, {
    day: 'Wed',
    amount: 520000
  }, {
    day: 'Thu',
    amount: 890000
  }, {
    day: 'Fri',
    amount: 750000
  }, {
    day: 'Sat',
    amount: 920000
  }, {
    day: 'Sun',
    amount: 610000
  }];
  const maxAmount = Math.max(...salesData.map(d => d.amount));
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-1642">
      <Header title="Analytics & Reports" data-id="element-1643" />

      <div className="p-4 space-y-4" data-id="element-1644">
        {/* Period Selector */}
        <div className="flex gap-2" data-id="element-1645">
          {(['7d', '30d', '90d'] as const).map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${period === p ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-1646">
              {p === '7d' ? 'Last 7 Days' : p === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
            </button>)}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3" data-id="element-1647">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-1648">
            <div className="flex items-center gap-2 mb-2" data-id="element-1649">
              <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center" data-id="element-1650">
                <DollarSign className="w-4 h-4 text-success" data-id="element-1651" />
              </div>
              <span className="text-xs text-gray-500" data-id="element-1652">Total Revenue</span>
            </div>
            <p className="text-xl font-bold text-gray-900" data-id="element-1653">
              ₦{(totalRevenue / 1000).toFixed(0)}k
            </p>
            <div className="flex items-center gap-1 mt-1" data-id="element-1654">
              <TrendingUp className="w-3 h-3 text-success" data-id="element-1655" />
              <span className="text-xs text-success" data-id="element-1656">+12.5%</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-1657">
            <div className="flex items-center gap-2 mb-2" data-id="element-1658">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center" data-id="element-1659">
                <Package className="w-4 h-4 text-primary" data-id="element-1660" />
              </div>
              <span className="text-xs text-gray-500" data-id="element-1661">Total Orders</span>
            </div>
            <p className="text-xl font-bold text-gray-900" data-id="element-1662">{totalOrders}</p>
            <div className="flex items-center gap-1 mt-1" data-id="element-1663">
              <TrendingUp className="w-3 h-3 text-success" data-id="element-1664" />
              <span className="text-xs text-success" data-id="element-1665">+8.2%</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-1666">
            <div className="flex items-center gap-2 mb-2" data-id="element-1667">
              <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center" data-id="element-1668">
                <ShoppingCart className="w-4 h-4 text-accent" data-id="element-1669" />
              </div>
              <span className="text-xs text-gray-500" data-id="element-1670">Avg Order Value</span>
            </div>
            <p className="text-xl font-bold text-gray-900" data-id="element-1671">
              ₦{(avgOrderValue / 1000).toFixed(0)}k
            </p>
            <div className="flex items-center gap-1 mt-1" data-id="element-1672">
              <TrendingDown className="w-3 h-3 text-error" data-id="element-1673" />
              <span className="text-xs text-error" data-id="element-1674">-2.1%</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-1675">
            <div className="flex items-center gap-2 mb-2" data-id="element-1676">
              <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center" data-id="element-1677">
                <Eye className="w-4 h-4 text-warning" data-id="element-1678" />
              </div>
              <span className="text-xs text-gray-500" data-id="element-1679">Store Views</span>
            </div>
            <p className="text-xl font-bold text-gray-900" data-id="element-1680">{totalViews}</p>
            <div className="flex items-center gap-1 mt-1" data-id="element-1681">
              <TrendingUp className="w-3 h-3 text-success" data-id="element-1682" />
              <span className="text-xs text-success" data-id="element-1683">+15.3%</span>
            </div>
          </div>
        </div>

        {/* Sales Chart */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-1684">
          <h3 className="font-bold text-gray-900 mb-4" data-id="element-1685">Sales Overview</h3>
          <div className="flex items-end justify-between gap-2 h-40" data-id="element-1686">
            {salesData.map((data, idx) => <div key={data.day} className="flex-1 flex flex-col items-center gap-2" data-id="element-1687">
                <motion.div initial={{
              height: 0
            }} animate={{
              height: `${data.amount / maxAmount * 100}%`
            }} transition={{
              delay: idx * 0.1,
              type: 'spring'
            }} className="w-full bg-gradient-to-t from-primary to-primary-400 rounded-t-lg relative group" data-id="element-1688">
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap" data-id="element-1689">
                    ₦{(data.amount / 1000).toFixed(0)}k
                  </div>
                </motion.div>
                <span className="text-xs text-gray-500" data-id="element-1690">{data.day}</span>
              </div>)}
          </div>
        </div>

        {/* Conversion Rate */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-1691">
          <div className="flex items-center justify-between mb-3" data-id="element-1692">
            <h3 className="font-bold text-gray-900" data-id="element-1693">Conversion Rate</h3>
            <span className="text-2xl font-bold text-primary" data-id="element-1694">
              {conversionRate}%
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3" data-id="element-1695">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${conversionRate}%`
          }} transition={{
            delay: 0.5,
            type: 'spring'
          }} className="bg-gradient-to-r from-primary to-primary-400 h-3 rounded-full" data-id="element-1696" />
          </div>
          <p className="text-xs text-gray-500 mt-2" data-id="element-1697">
            {conversionRate}% of visitors make a purchase
          </p>
        </div>

        {/* Top Selling Products */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-1698">
          <h3 className="font-bold text-gray-900 mb-3" data-id="element-1699">Top Selling Products</h3>
          <div className="space-y-3" data-id="element-1700">
            {topProducts.map((product, idx) => <div key={product.id} className="flex items-center gap-3" data-id="element-1701">
                <span className="text-sm font-bold text-gray-400 w-6" data-id="element-1702">
                  #{idx + 1}
                </span>
                <img src={product.images[0]} alt={product.name} className="w-12 h-12 rounded-lg object-cover" data-id="element-1703" />
                <div className="flex-1 min-w-0" data-id="element-1704">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1705">
                    {product.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1706">{product.category}</p>
                </div>
                <div className="text-right" data-id="element-1707">
                  <p className="text-sm font-bold text-primary" data-id="element-1708">
                    ₦{(product.price / 1000).toFixed(0)}k
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1709">
                    {Math.floor(Math.random() * 50 + 10)} sold
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </motion.div>;
}